/**
 * HubSpot sync — preview, review field by field, then confirm.
 *
 * Opens straight into a preview because these are writes into a partner's CRM and
 * undoing them is manual work for CEG. Every proposed property is listed and can
 * be denied individually; everything is approved by default, so the fast path is
 * still one click.
 *
 * Only `vrp_founder_profile_id` cannot be denied — it is the dedupe key, and
 * without it the next run would not recognise the record and would duplicate it.
 * The backend enforces that too; this UI only reflects it.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { getHubSpotStatus, previewHubSpotSync, confirmHubSpotSync } from '../services/apiService';

const STATUS_STYLES = {
  would_create:       { background: '#e0f2fe', color: '#0369a1', label: 'Will create' },
  created:            { background: '#dcfce7', color: '#15803d', label: 'Created' },
  would_update:       { background: '#ede9fe', color: '#6d28d9', label: 'Will update' },
  updated:            { background: '#dcfce7', color: '#15803d', label: 'Updated' },
  already_in_hubspot: { background: '#f1f5f9', color: '#475569', label: "HubSpot's — untouched" },
  up_to_date:         { background: '#f1f5f9', color: '#475569', label: 'Up to date' },
  failed:             { background: '#fee2e2', color: '#b91c1c', label: 'Failed' },
  skipped:            { background: '#fff8e1', color: '#7a5c00', label: 'Skipped' },
};

const ACTION_LABELS = {
  new:       { text: 'new',       color: '#0369a1' },
  fill:      { text: 'fill',      color: '#15803d' },
  // VRP's value differs from what we last wrote, so this is fresh information
  // rather than a blind re-push.
  changed:   { text: 'changed',   color: '#6d28d9' },
  overwrite: { text: 'overwrite', color: '#b45309' },
};

const WRITABLE = ['would_create', 'created', 'would_update', 'updated'];
const fieldKey = (f) => `${f.object}.${f.name}`;

export default function HubSpotSyncModal({ onClose }) {
  const [status, setStatus]     = useState(null);
  const [result, setResult]     = useState(null);
  const [busy, setBusy]         = useState('preview');
  const [error, setError]       = useState(null);
  const [done, setDone]         = useState(false);
  // profile_id -> Set of denied "object.name" keys. Absent means nothing denied.
  const [denied, setDenied]     = useState({});
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [s, p] = await Promise.all([getHubSpotStatus(), previewHubSpotSync()]);
        setStatus(s);
        setResult(p);
      } catch (err) {
        setError(err.response?.data?.detail || 'Could not load the preview.');
      } finally {
        setBusy(null);
      }
    })();
  }, []);

  const writable = useMemo(
    () => (result?.results || []).filter((r) => WRITABLE.includes(r.status)),
    [result],
  );

  // Only rows that need attention are listed. Two kinds of no-op are counted in
  // the summary but not enumerated: records HubSpot owns (never touched), and
  // records we own that already match VRP. Listing either buries the handful
  // that matter under the whole cohort.
  // `skipped` and `failed` stay visible: both are problems to look at.
  const visible = useMemo(
    () => (result?.results || []).filter(
      (r) => r.status !== 'already_in_hubspot' && r.status !== 'up_to_date'),
    [result],
  );

  function isDenied(profileId, f) {
    return !f.locked && !!denied[profileId]?.has(fieldKey(f));
  }

  function toggleField(profileId, f) {
    if (f.locked) return;
    setDenied((prev) => {
      const next = new Set(prev[profileId] || []);
      const key = fieldKey(f);
      if (next.has(key)) next.delete(key); else next.add(key);
      return { ...prev, [profileId]: next };
    });
  }

  function setAllForFounder(r, denyAll) {
    setDenied((prev) => ({
      ...prev,
      [r.profile_id]: denyAll
        ? new Set(r.fields.filter((f) => !f.locked).map(fieldKey))
        : new Set(),
    }));
  }

  const approvedCount = writable.reduce(
    (n, r) => n + r.fields.filter((f) => !isDenied(r.profile_id, f)).length, 0,
  );
  const deniedCount = writable.reduce(
    (n, r) => n + r.fields.filter((f) => isDenied(r.profile_id, f)).length, 0,
  );

  async function handleConfirm() {
    setBusy('sync');
    setError(null);
    try {
      const selections = writable.map((r) => {
        const keep = r.fields.filter((f) => !isDenied(r.profile_id, f));
        return {
          profile_id: r.profile_id,
          company: keep.filter((f) => f.object === 'company').map((f) => f.name),
          contact: keep.filter((f) => f.object === 'contact').map((f) => f.name),
        };
      });
      setResult(await confirmHubSpotSync(selections));
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  }

  const counts     = result?.counts || {};
  const willCreate = counts.would_create || 0;
  const willUpdate = counts.would_update || 0;
  const untouched  = counts.already_in_hubspot || 0;
  const upToDate   = counts.up_to_date || 0;
  const canSync = status?.enabled && writable.length > 0 && approvedCount > 0 && !done;

  return (
    <div style={s.backdrop} onClick={onClose}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        <div style={s.header}>
          <h2 style={s.title}>{done ? 'Sync complete' : 'Sync to HubSpot'}</h2>
          <button style={s.close} onClick={onClose} aria-label="Close">×</button>
        </div>

        {busy === 'preview' && <p style={s.muted}>Building preview…</p>}

        {result && (
          <>
            <p style={s.summary}>
              {done
                ? `${counts.created || 0} created, ${counts.updated || 0} updated`
                  + (counts.failed ? `, ${counts.failed} failed` : '')
                : `${willCreate} to create, ${willUpdate} to update — `
                  + `${approvedCount} field${approvedCount === 1 ? '' : 's'} approved`
                  + (deniedCount ? `, ${deniedCount} denied` : '')}
              {(untouched > 0 || upToDate > 0) && (
                <span style={s.mutedInline}>
                  {untouched > 0 && ` ${untouched} belong to HubSpot and are left untouched.`}
                  {upToDate > 0 && ` ${upToDate} already match VRP.`}
                </span>
              )}
            </p>

            {!done && (
              <p style={s.gapNote}>
                Everything is approved by default — untick anything you don't want
                written. Records that already existed in HubSpot are never touched,
                and the dedupe key can't be denied.
              </p>
            )}

            {!status?.enabled && !done && (
              <p style={s.warn}>
                Sync is disabled on the server. Set <code>HUBSPOT_SYNC_ENABLED=true</code>{' '}
                and <code>HUBSPOT_ACCESS_TOKEN</code>, then restart the backend.
                This preview is safe and writes nothing.
              </p>
            )}

            {counts.skipped > 0 && (
              <p style={s.warn}>
                {counts.skipped} skipped — see the reason on each row.
              </p>
            )}

            <div style={s.list}>
              {visible.length === 0 && (
                <p style={s.emptyList}>
                  Nothing to sync — every VRP founder is already represented in
                  HubSpot by a record the portal doesn't own.
                </p>
              )}
              {visible.map((r) => {
                const st = STATUS_STYLES[r.status] || STATUS_STYLES.skipped;
                const canReview = !done && WRITABLE.includes(r.status) && r.fields?.length > 0;
                const open = !!expanded[r.profile_id];
                const kept = (r.fields || []).filter((f) => !isDenied(r.profile_id, f)).length;
                return (
                  <div key={r.profile_id} style={s.row}>
                    <div style={s.rowMain}>
                      <span style={s.company}>{r.company_name || '(no company name)'}</span>
                      <span style={s.email}>{r.email}</span>
                    </div>
                    <div style={s.rowMeta}>
                      {canReview && (
                        <button
                          style={s.disclosure}
                          onClick={() => setExpanded((p) => ({ ...p, [r.profile_id]: !open }))}
                        >
                          {open ? '▾' : '▸'} {kept} of {r.fields.length} fields
                        </button>
                      )}
                      {done && WRITABLE.includes(r.status) && (
                        <span style={s.props}>
                          {r.company_property_count + r.contact_property_count} fields written
                        </span>
                      )}
                      {r.fields_preserved > 0 && !done && (
                        <span style={s.props}>{r.fields_preserved} hand-written, kept</span>
                      )}
                      <span style={{ ...s.pill, background: st.background, color: st.color }}>
                        {st.label}
                      </span>
                    </div>

                    {r.reason && <p style={s.reason}>{r.reason}</p>}
                    {r.error && <p style={s.rowError}>{r.error}</p>}
                    {r.dropped?.length > 0 && (
                      <p style={s.dropped}>
                        {r.dropped.length} value{r.dropped.length === 1 ? '' : 's'} dropped:{' '}
                        {r.dropped.map((d) => d.replace(/^HubSpot mapping: /, '')).join('; ')}
                      </p>
                    )}

                    {canReview && open && (
                      <div style={s.fieldBox}>
                        <div style={s.fieldActions}>
                          <button style={s.linkBtn} onClick={() => setAllForFounder(r, false)}>
                            Approve all
                          </button>
                          <span style={s.divider}>·</span>
                          <button style={s.linkBtn} onClick={() => setAllForFounder(r, true)}>
                            Deny all
                          </button>
                        </div>
                        {r.fields.map((f) => {
                          const off = isDenied(r.profile_id, f);
                          const act = ACTION_LABELS[f.action] || ACTION_LABELS.new;
                          return (
                            <label
                              key={fieldKey(f)}
                              style={{ ...s.fieldRow, opacity: off ? 0.45 : 1 }}
                            >
                              <input
                                type="checkbox"
                                checked={!off}
                                disabled={f.locked}
                                onChange={() => toggleField(r.profile_id, f)}
                              />
                              <span style={s.fieldObj}>
                                {f.object === 'company' ? 'Co' : 'Ct'}
                              </span>
                              <span style={s.fieldName}>
                                {f.name}
                                {f.locked && (
                                  <span style={s.lock} title="Required — the dedupe key"> 🔒</span>
                                )}
                              </span>
                              <span style={{ ...s.action, color: act.color }}>{act.text}</span>
                              <span style={s.fieldValue}>
                                {f.current != null && (
                                  <span style={s.oldValue}>{f.current} → </span>
                                )}
                                {f.value === null || f.value === ''
                                  ? <em>(empty)</em>
                                  : String(f.value)}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}

                    {r.company_url && (
                      <a href={r.company_url} target="_blank" rel="noreferrer" style={s.link}>
                        View in HubSpot ↗
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {error && <p style={s.error}>{error}</p>}

        <div style={s.footer}>
          <button style={s.cancel} onClick={onClose}>{done ? 'Close' : 'Cancel'}</button>
          {!done && (
            <button
              style={{ ...s.confirm, opacity: canSync && !busy ? 1 : 0.45,
                       cursor: canSync && !busy ? 'pointer' : 'not-allowed' }}
              onClick={handleConfirm}
              disabled={!canSync || !!busy}
            >
              {busy === 'sync'
                ? 'Syncing…'
                : `Sync ${approvedCount} field${approvedCount === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  backdrop:    { position: 'fixed', inset: 0, background: 'rgba(15,32,20,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 'var(--space-4)' },
  modal:       { background: 'var(--color-frost)', borderRadius: 'var(--radius-lg)', boxShadow: '0 12px 40px rgba(0,0,0,0.25)', width: '100%', maxWidth: 860, maxHeight: '88vh', display: 'flex', flexDirection: 'column', padding: 'var(--space-5)' },
  header:      { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-3)' },
  title:       { fontFamily: 'var(--font-accent)', fontSize: 24, fontWeight: 800, color: 'var(--color-rust)' },
  close:       { background: 'none', border: 'none', fontSize: 26, lineHeight: 1, color: 'var(--color-forest)', cursor: 'pointer', opacity: 0.6 },
  summary:     { fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--color-forest)', marginBottom: 6 },
  mutedInline: { opacity: 0.6 },
  gapNote:     { fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--color-forest)', opacity: 0.65, marginBottom: 'var(--space-3)', lineHeight: 1.5 },
  muted:       { fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--color-forest)', opacity: 0.6, padding: 'var(--space-4) 0' },
  warn:        { fontFamily: 'var(--font-body)', fontSize: 13, color: '#7a5c00', background: '#fff8e1', borderRadius: 'var(--radius-sm)', padding: '10px 12px', marginBottom: 'var(--space-3)', lineHeight: 1.5 },
  list:        { overflowY: 'auto', flex: 1, border: '1px solid var(--color-mint-dark)', borderRadius: 'var(--radius-sm)', background: '#fff' },
  emptyList:   { padding: 'var(--space-5)', textAlign: 'center', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', opacity: 0.55, lineHeight: 1.5 },
  row:         { padding: '12px 14px', borderBottom: '1px solid var(--color-mint-dark)' },
  rowMain:     { display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' },
  rowMeta:     { display: 'flex', alignItems: 'center', gap: 10, marginTop: 5, flexWrap: 'wrap' },
  company:     { fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 700, color: 'var(--color-forest)' },
  email:       { fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--color-forest)', opacity: 0.6 },
  props:       { fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--color-forest)', opacity: 0.55 },
  pill:        { fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 'var(--radius-full)', marginLeft: 'auto' },
  disclosure:  { fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700, color: 'var(--color-forest)', background: 'var(--color-mint-dark)', border: 'none', borderRadius: 'var(--radius-full)', padding: '2px 10px', cursor: 'pointer' },
  reason:      { fontFamily: 'var(--font-body)', fontSize: 12, color: '#475569', marginTop: 4 },
  rowError:    { fontFamily: 'var(--font-body)', fontSize: 12, color: '#b91c1c', marginTop: 4 },
  dropped:     { fontFamily: 'var(--font-body)', fontSize: 11, color: '#7a5c00', marginTop: 4, lineHeight: 1.45 },
  fieldBox:    { marginTop: 8, borderTop: '1px dashed var(--color-mint-dark)', paddingTop: 8 },
  fieldActions:{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 },
  linkBtn:     { fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700, color: 'var(--color-forest)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' },
  divider:     { fontSize: 11, opacity: 0.4 },
  fieldRow:    { display: 'grid', gridTemplateColumns: '16px 22px minmax(150px, 1.1fr) 64px minmax(0, 2fr)', alignItems: 'baseline', gap: 8, padding: '3px 0', fontFamily: 'var(--font-body)', fontSize: 11.5, cursor: 'pointer' },
  fieldObj:    { color: 'var(--color-forest)', opacity: 0.45, fontWeight: 700, fontSize: 10 },
  fieldName:   { color: 'var(--color-forest)', fontWeight: 600, overflowWrap: 'anywhere' },
  lock:        { fontSize: 9 },
  action:      { fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.03em' },
  fieldValue:  { color: 'var(--color-forest)', opacity: 0.75, overflowWrap: 'anywhere' },
  oldValue:    { textDecoration: 'line-through', opacity: 0.5 },
  link:        { fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--color-forest)', display: 'inline-block', marginTop: 5 },
  error:       { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-rust)', marginTop: 'var(--space-3)' },
  footer:      { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 'var(--space-4)' },
  cancel:      { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700, color: 'var(--color-forest)', background: 'var(--color-mint-dark)', border: 'none', borderRadius: 'var(--radius-sm)', padding: '9px 18px', cursor: 'pointer' },
  confirm:     { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700, color: '#fff', background: 'var(--color-forest)', border: 'none', borderRadius: 'var(--radius-sm)', padding: '9px 18px' },
};
